import {
  OpenAICompatibleModelProvider,
  currentSubmissionContext,
  registerModelProvider,
} from "@fabric-harness/sdk";
import type {
  ModelMetadata,
  ModelProvider,
  ModelProviderFactory,
  ModelRequest,
  ModelResponse,
  ModelStreamChunk,
} from "@fabric-harness/sdk";
import { databricksIdentity, databricksPrincipalFromEnv } from "./identity.js";
import { databricksWorkspaceOrigin } from "./workspace-origin.js";

export type DatabricksInferenceMode = "auto" | "ai-gateway" | "serving-endpoints";

export type DatabricksRequestTags =
  | Record<string, string>
  | (() => Record<string, string> | Promise<Record<string, string>>);

export interface DatabricksModelOptions {
  /** Workspace host, e.g. https://adb-1234567890.0.cloud.databricks.com. */
  host: string;
  /**
   * Bearer token, or a resolver awaited per request. Pass a resolver for OAuth principal tokens
   * (service-principal / on-behalf-of) so long-lived agents don't pin a stale token.
   */
  token: string | (() => string | Promise<string>);
  /** Serving endpoint or fully-qualified Unity AI Gateway model service. */
  defaultModel?: string;
  /** Select routing explicitly. `auto` sends `system.ai.*` through Unity AI Gateway. */
  mode?: DatabricksInferenceMode;
  /** Explicit OpenAI-compatible base URL. Overrides `mode` URL construction. */
  baseUrl?: string;
  headers?:
    | Record<string, string>
    | (() => Record<string, string> | Promise<Record<string, string>>);
  /** Tags recorded by Unity AI Gateway usage and inference tables. */
  requestTags?: DatabricksRequestTags;
  /** Optional model metadata used by context budgeting and operator UIs. */
  modelMetadata?: Record<string, Omit<ModelMetadata, "provider" | "model" | "gateway">>;
  /** Fetch implementation for tests or controlled private-network transports. */
  fetchImpl?: typeof fetch;
}

/** Build the OpenAI-compatible base URL used for one Databricks inference mode. */
export function databricksModelBaseUrl(
  host: string,
  mode: Exclude<DatabricksInferenceMode, "auto">,
): string {
  const normalized = databricksWorkspaceOrigin(host);
  return mode === "ai-gateway"
    ? `${normalized}/ai-gateway/mlflow/v1`
    : `${normalized}/serving-endpoints`;
}

/** True for Unity Catalog model-service identifiers accepted by Unity AI Gateway. */
export function isDatabricksModelService(model: string | undefined): boolean {
  return model?.startsWith("system.ai.") ?? false;
}

class DatabricksModelProvider implements ModelProvider {
  readonly name = "databricks";
  private readonly options: DatabricksModelOptions;
  private readonly gateway: OpenAICompatibleModelProvider;
  private readonly serving: OpenAICompatibleModelProvider;
  private readonly explicit: OpenAICompatibleModelProvider | undefined;

  constructor(options: DatabricksModelOptions) {
    this.options = options;
    const common = {
      name: this.name,
      apiKey: typeof options.token === "string" ? options.token : "",
      ...(typeof options.token === "function" ? { tokenProvider: options.token } : {}),
      ...(options.defaultModel !== undefined ? { defaultModel: options.defaultModel } : {}),
      ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
    };
    this.gateway = new OpenAICompatibleModelProvider({
      ...common,
      baseUrl: databricksModelBaseUrl(options.host, "ai-gateway"),
      headers: () => this.resolveHeaders(true),
    });
    this.serving = new OpenAICompatibleModelProvider({
      ...common,
      baseUrl: databricksModelBaseUrl(options.host, "serving-endpoints"),
      headers: () => this.resolveHeaders(false),
    });
    this.explicit = options.baseUrl
      ? new OpenAICompatibleModelProvider({
          ...common,
          baseUrl: options.baseUrl,
          headers: () => this.resolveHeaders(options.baseUrl?.includes("/ai-gateway/") ?? false),
        })
      : undefined;
  }

  generate(request: ModelRequest): Promise<ModelResponse> {
    return this.providerFor(request.model).generate(request);
  }

  stream(request: ModelRequest): AsyncIterable<ModelStreamChunk> {
    const provider = this.providerFor(request.model);
    if (!provider.stream)
      throw new Error("databricks: selected provider does not support streaming.");
    return provider.stream(request);
  }

  getModelMetadata(model = this.options.defaultModel): ModelMetadata {
    const mode = this.modeFor(model);
    return {
      provider: this.name,
      ...(model ? { model } : {}),
      gateway: this.options.baseUrl ?? databricksModelBaseUrl(this.options.host, mode),
      ...(model && this.options.modelMetadata?.[model] ? this.options.modelMetadata[model] : {}),
    };
  }

  private providerFor(model = this.options.defaultModel): OpenAICompatibleModelProvider {
    if (this.explicit) return this.explicit;
    return this.modeFor(model) === "ai-gateway" ? this.gateway : this.serving;
  }

  private modeFor(model: string | undefined): Exclude<DatabricksInferenceMode, "auto"> {
    const configured = this.options.mode ?? "auto";
    if (configured !== "auto") return configured;
    return isDatabricksModelService(model) ? "ai-gateway" : "serving-endpoints";
  }

  private async resolveHeaders(gateway: boolean): Promise<Record<string, string>> {
    const configured =
      typeof this.options.headers === "function"
        ? await this.options.headers()
        : (this.options.headers ?? {});
    if (!gateway) return configured;
    const supplied =
      typeof this.options.requestTags === "function"
        ? await this.options.requestTags()
        : (this.options.requestTags ?? {});
    const context = currentSubmissionContext();
    const tags = compactTags({
      ...(context?.submissionId ? { submission_id: context.submissionId } : {}),
      ...(context?.attemptId ? { attempt_id: context.attemptId } : {}),
      ...(context?.tenantId ? { tenant_id: context.tenantId } : {}),
      ...(context?.agent ? { agent: context.agent } : {}),
      ...supplied,
    });
    return Object.keys(tags).length > 0
      ? { ...configured, "Databricks-Ai-Gateway-Request-Tags": JSON.stringify(tags) }
      : configured;
  }
}

function compactTags(tags: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(tags)
      .filter(([key, value]) => key.length > 0 && value.length > 0)
      .map(([key, value]) => [key.slice(0, 128), value.slice(0, 512)]),
  );
}

/**
 * Databricks inference provider supporting both Unity AI Gateway model services and custom
 * Model Serving endpoints. In `auto` mode, fully-qualified `system.ai.*` models use AI Gateway and
 * other endpoint names retain the `/serving-endpoints` route.
 */
export function databricksFoundationModelProvider(options: DatabricksModelOptions): ModelProvider {
  return new DatabricksModelProvider(options);
}

function inferenceMode(value: string | undefined): DatabricksInferenceMode | undefined {
  if (!value) return undefined;
  if (value === "auto" || value === "ai-gateway" || value === "serving-endpoints") return value;
  throw new Error(
    `Invalid DATABRICKS_INFERENCE_MODE "${value}". Use auto, ai-gateway, or serving-endpoints.`,
  );
}

function origin(value: string): string {
  try {
    return new URL(value).origin;
  } catch {
    return value;
  }
}

const factory: ModelProviderFactory = (parsed, providers, env, warnings) => {
  const settings = providers?.databricks;
  const configuredUrl = settings?.baseUrl;
  const rawHost = env.DATABRICKS_HOST ?? (configuredUrl ? origin(configuredUrl) : undefined);
  if (!rawHost)
    throw new Error(
      "Missing Databricks host. Set DATABRICKS_HOST or providers.databricks.baseUrl.",
    );
  const host = rawHost.includes("://") ? rawHost : `https://${rawHost}`;
  const staticToken =
    settings?.apiKey?.trim() ||
    env[settings?.apiKeyEnv ?? "DATABRICKS_TOKEN"]?.trim() ||
    env.DATABRICKS_API_KEY?.trim();
  let principal: ReturnType<typeof databricksPrincipalFromEnv> | undefined;
  if (!staticToken) {
    try {
      principal = databricksPrincipalFromEnv(env);
    } catch {}
  }
  if (!staticToken && !principal)
    throw new Error(
      "Missing Databricks credentials. Set DATABRICKS_TOKEN, DATABRICKS_CLIENT_ID + DATABRICKS_CLIENT_SECRET, or providers.databricks.apiKey/apiKeyEnv.",
    );
  const token = staticToken ?? databricksIdentity(principal as NonNullable<typeof principal>);
  const explicitBaseUrl =
    env.DATABRICKS_AI_GATEWAY_BASE_URL ??
    (configuredUrl && new URL(configuredUrl).pathname !== "/" ? configuredUrl : undefined);
  const mode = inferenceMode(env.DATABRICKS_INFERENCE_MODE);
  return {
    model: parsed.modelId,
    provider: databricksFoundationModelProvider({
      host,
      token,
      defaultModel: parsed.modelId,
      ...(explicitBaseUrl ? { baseUrl: explicitBaseUrl } : {}),
      ...(mode ? { mode } : {}),
      ...(settings?.headers ? { headers: settings.headers } : {}),
    }),
    providerName: "databricks",
    modelId: parsed.modelId,
    warnings,
  };
};

let registered = false;

/** Register `databricks/<model>` model references. */
export function registerDatabricksModelProvider(): void {
  if (registered) return;
  registered = true;
  registerModelProvider("databricks", factory);
}
